
import { Field } from "@/types/Field";
import { boundsInWrongOrder } from "@/lib/additionalFieldConstraints";
import { createAdditionalField } from "@/lib/api/clients/AdditionalFieldClient";
import { handleErrorServer, ServerError } from "@/lib/api/errors/ErrorHandler";
import { DataType } from "@/types/enums/DataType";
import { isValidObjectId } from "@/lib/utils/input-validator";
import {
  deleteAdditionalField,
  updateAdditionalField,
  getAdditionalFieldOptionUsage,
} from "@/lib/api/clients/AdditionalFieldClient";
import { GuestFields } from "@/types/enums/GuestFields";

export async function createField(
  eventId: string,
  field: Field,
  /** Art. 50 KI-VO (J6): forwarded to the client as `?aiContentTool=`. */
  aiContentTool?: string,
): Promise<string | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    if (!field.name) throw new Error("validationError");
    if (!field.dataType) throw new Error("validationError");
    if (!field.additionalField) throw new Error("validationError");

    if (field.lowerBound && field.upperBound) {
      if (boundsInWrongOrder(field.lowerBound, field.upperBound))
        throw new Error("validationError");
    }

    if (
      field.dataType === DataType.SINGLE_SELECT ||
      field.dataType === DataType.MULTI_SELECT
    )
      if ((field.options?.length ?? 0) <= 0) throw new Error("validationError");

    // Guest-built-in name collision guard — GUEST-target definitions only.
    // A SPEAKER/SPONSOR definition legitimately may share a name with a guest
    // built-in ("company", "email", …): those live in a different value map
    // on a different collection, and the backend validates per target.
    const targetsGuest = (field.appliesTo ?? "GUEST") === "GUEST";
    const allGuestFields = Object.values(GuestFields) as string[];

    if (targetsGuest && allGuestFields.includes(field.name))
      throw new Error("invalidFieldName");

    // Only an AI lane adds the argument: every other caller keeps its exact
    // call shape (and request URL).
    return aiContentTool
      ? await createAdditionalField(eventId, field, aiContentTool)
      : await createAdditionalField(eventId, field);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function updateField(
  eventId: string,
  fieldId: string,
  field: Partial<Field>
): Promise<void | ServerError> {
  try {

    // Validation
    if (!eventId || !fieldId || !field) throw new Error("validationError");

    return await updateAdditionalField(eventId, fieldId, field);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function deleteField(
  eventId: string,
  fieldId: string
): Promise<void | ServerError> {
  try {

    // Validation
    if (!eventId || !fieldId) throw new Error("validationError");

    return await deleteAdditionalField(eventId, fieldId);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Fetch per-option guest usage counts. Returns `null` on any failure so the
 * caller can fall back to a count-less generic removal warning instead of
 * assuming zero guests are affected (which would silently drop values).
 */
export async function fetchOptionUsage(
  eventId: string,
  fieldId: string
): Promise<Record<string, number> | null> {
  try {
    if (!eventId || !fieldId) return null;
    return await getAdditionalFieldOptionUsage(eventId, fieldId);
  } catch {
    return null;
  }
}
